import { FC, ReactElement } from "react";
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import TablePagination from "@/components/paginations/table-pagination";
import { numberWithCommas } from "@/utils";
import { tariff_code_placeholder } from "@/constants";

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  FULLY_CLAIMED: { label: "Fully Claimed", color: "#2E7D32", bg: "#E8F5E9" },
  PARTIALLY_CLAIMED: { label: "Partial", color: "#ED6C02", bg: "#FFF3E0" },
  NOT_CLAIMED: { label: "Not Claimed", color: "#D32F2F", bg: "#FFEBEE" },
};

interface OpportunitiesTableProps {
  data: PrefOriginOpportunity[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newSize: number) => void;
  onRowClick: (row: PrefOriginOpportunity) => void;
}

const OpportunitiesTable: FC<OpportunitiesTableProps> = ({
  data,
  isLoading,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onRowClick,
}): ReactElement => {
  const headers = [
    "HTS Code",
    "Country",
    "Program",
    "Status",
    "Total Entries",
    "Claimed / Unclaimed",
    "Goods Value",
    "Potential Savings",
    "",
  ];

  const colCount = headers.length;

  return (
    <Stack spacing={0}>
      <Box
        sx={{
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          overflow: "hidden",
          boxShadow: tokens.shadow.sm,
          ...tableCompactSx,
        }}
      >
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {headers.map((h) => (
                  <TableCell
                    key={h || "__arrow"}
                    align={h === "" ? "center" : "left"}
                    sx={h === "" ? { width: 48 } : {}}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from({ length: colCount }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="rounded" height={18} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ border: "none" }}>
                    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 6 }}>
                      <img src={tariff_code_placeholder} alt="No data" style={{ width: 120, height: 120, opacity: 0.6 }} />
                      <Typography variant="h6" fontWeight={tokens.fontWeight.semibold} color={tokens.text.primary}>
                        No Opportunities Found
                      </Typography>
                      <Typography variant="body2" sx={{ color: tokens.text.secondary, maxWidth: 420, textAlign: "center", lineHeight: 1.6 }}>
                        No preferential origin opportunities found for the selected filters. Try adjusting your date range or search criteria.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, idx) => {
                  const statusCfg = statusConfig[row.status] ?? {
                    label: row.status,
                    color: tokens.text.secondary,
                    bg: tokens.bg.hover,
                  };
                  return (
                    <TableRow
                      key={row.htsNumberFull ?? idx}
                      onClick={() => onRowClick(row)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: tokens.bg.hover },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: tokens.fontSize.base,
                            fontFamily: "monospace",
                            fontWeight: tokens.fontWeight.bold,
                            color: tokens.text.primary,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.htsNumberFull}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
                          {row.countryName || row.countryOfOriginCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: tokens.fontSize.base,
                            color: tokens.text.primary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {row.program}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            fontSize: tokens.fontSize.sm2,
                            fontWeight: tokens.fontWeight.semibold,
                            color: statusCfg.color,
                            backgroundColor: statusCfg.bg,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {statusCfg.label}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
                          {numberWithCommas(row.totalEntries)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
                          {numberWithCommas(row.claimedEntries)} / {numberWithCommas(row.unclaimedEntries)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
                          ${numberWithCommas(row.goodsValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: tokens.fontSize.base,
                            fontWeight: tokens.fontWeight.bold,
                            color: row.potentialSavings > 0 ? "#2E7D32" : tokens.text.primary,
                          }}
                        >
                          ${numberWithCommas(row.potentialSavings)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 48 }}>
                        <KeyboardArrowRightIcon
                          sx={{
                            fontSize: 20,
                            color: tokens.text.muted,
                            transition: "color 150ms ease",
                            ".MuiTableRow-root:hover &": {
                              color: tokens.brand.primary,
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!isLoading && total > 0 && (
          <Box borderTop={`1px solid ${tokens.border.light}`}>
            <TablePagination
              limit={pageSize}
              page={page}
              total={total}
              handlePageChange={onPageChange}
              handleLimitChange={onPageSizeChange}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default OpportunitiesTable;
